import React, { useState, useEffect } from "react";
import axios from "axios";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  BookOpen, RefreshCw, Send, Rss, ChevronDown, ChevronUp, Trash2, Clock,
  ExternalLink, Share2, PenLine, Mail, X, Monitor, Smartphone, FlaskConical,
  Link, Plus, Minus,
} from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = BACKEND_URL + "/api";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtDatetime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/** Render newsletter markdown with italic, bold, headings and line breaks. */
function renderNLContent(text, dm) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    if (line.startsWith("### ")) return (
      <h4 key={i} className={`font-semibold mt-4 mb-1 text-sm ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{line.slice(4)}</h4>
    );
    if (line.startsWith("## ")) return (
      <h3 key={i} className={`font-semibold mt-5 mb-1.5 text-base ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{line.slice(3)}</h3>
    );
    if (line.startsWith("# ")) return (
      <h2 key={i} className={`font-bold mt-5 mb-2 text-lg ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{line.slice(2)}</h2>
    );
    if (line.startsWith("---")) return (
      <hr key={i} className={`my-4 ${dm ? "border-white/8" : "border-black/8"}`} />
    );
    if (!line.trim()) return <div key={i} className="h-3" />;

    // Full-line italic: *paragraph text* (editorial sections)
    const italicMatch = line.match(/^\*([^*].+[^*])\*$/) || line.match(/^\*(.+)\*$/);
    if (italicMatch) return (
      <p key={i} className={`italic mb-2.5 text-sm leading-relaxed ${dm ? "text-slate-400" : "text-[#5a6e72]"}`}>
        {italicMatch[1].replace(/\*\*(.+?)\*\*/g, "").trim()}
      </p>
    );

    // Inline bold rendering (split on **)
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className={`mb-2 text-sm leading-relaxed ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>
        {parts.map((part, j) => {
          const boldMatch = part.match(/^\*\*(.+)\*\*$/);
          return boldMatch ? <strong key={j} className={dm ? "text-slate-100" : "text-[#191c1d]"}>{boldMatch[1]}</strong> : part;
        })}
      </p>
    );
  });
}

export default function NewslettersTab({
  dm, newsletters, loadingNL, loadNewsletters,
  expandedNL, setExpandedNL, togglePublish, deleteNewsletter,
  sendBrevoNewsletter, setBeehiivPreview,
  sendingCampaign, publishingBeehiiv, authHeader,
  nlFeatured = [], loadingNlFeatured = false,
  loadNlFeatured, removeNlFeatured, clearNlFeatured,
}) {
  const [schedule, setSchedule] = useState({ enabled: false, day_of_week: 1, hour: 9, last_sent_at: null, trigger_on_brief_publish: false });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState("");
  // Force-Include URLs panel
  const [forceUrl, setForceUrl] = useState("");
  const [scrapingForce, setScrapingForce] = useState(false);
  const [forceArticles, setForceArticles] = useState([]);
  // Email HTML preview modal
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewNlTitle, setPreviewNlTitle] = useState("");
  const [previewNlId, setPreviewNlId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState({});
  const [mobilePreview, setMobilePreview] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  // Per-newsletter OG prompt state
  const [nlPrompts, setNlPrompts] = useState({});
  const [savingNlPrompt, setSavingNlPrompt] = useState({});
  // Per-newsletter subject override (editable before Brevo send)
  const [subjectOverrides, setSubjectOverrides] = useState({});
  // Newsletter settings: editorial note (one-shot) + persistent footer
  const [extras, setExtras] = useState({ editorial_note_md: "", footer_md: "" });
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [extrasSaving, setExtrasSaving] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);

  // Load schedule once on mount. authHeader is stable (JWT from context) and API is a module
  // constant — adding them to deps would cause infinite re-fetches. eslint-disable is intentional.
   
  useEffect(() => {
    axios.get(`${API}/admin/newsletter-schedule`, { headers: authHeader() })
      .then(r => setSchedule(r.data))
      .catch(() => {});
    // Load newsletter extras (editorial note + footer)
    setExtrasLoading(true);
    axios.get(`${API}/admin/newsletter-settings`, { headers: authHeader() })
      .then(r => setExtras({
        editorial_note_md: r.data.editorial_note_md || "",
        footer_md: r.data.footer_md || "",
      }))
      .catch(() => {})
      .finally(() => setExtrasLoading(false));
  }, []);

  const saveExtras = async () => {
    setExtrasSaving(true);
    try {
      await axios.put(`${API}/admin/newsletter-settings`, extras, { headers: authHeader() });
      toast_success("Newsletter settings saved");
    } catch (e) {
      toast_error("Failed to save newsletter settings");
    } finally {
      setExtrasSaving(false);
    }
  };

  // Sync OG prompts from loaded newsletters
  useEffect(() => {
    const init = {};
    newsletters.forEach(nl => { init[nl.id] = nl.og_image_prompt || ""; });
    setNlPrompts(init);
  }, [newsletters]);

  const scrapeForceUrl = async () => {
    const url = forceUrl.trim();
    if (!url) return;
    if (!url.startsWith("http")) { toast_error("Enter a valid URL starting with http(s)"); return; }
    setScrapingForce(true);
    try {
      const res = await axios.post(`${API}/admin/force-include-url`, { url, context: "newsletter" }, { headers: authHeader() });
      const art = res.data.article;
      setForceUrl("");
      if (loadNlFeatured) await loadNlFeatured();  // refresh persistent queue
      if (res.data.already_existed) {
        toast_success(`Article already in feed: "${art.title?.slice(0, 60)}"`);
      } else {
        toast_success(`Scraped & queued for next newsletter: "${art.title?.slice(0, 60)}"`);
      }
    } catch (e) {
      toast_error(e.response?.data?.detail || "Could not scrape URL — check it's publicly accessible.");
    } finally {
      setScrapingForce(false);
    }
  };

  const handleSaveSchedule = async () => {    setSavingSchedule(true);
    setScheduleMsg("");
    try {
      await axios.put(`${API}/admin/newsletter-schedule`, schedule, { headers: authHeader() });
      const triggerMsg = schedule.trigger_on_brief_publish ? " + trigger on brief publish" : "";
      setScheduleMsg(schedule.enabled
        ? `Auto-send ON — every ${DAYS[schedule.day_of_week]} at ${schedule.hour.toString().padStart(2, "0")}:00 UTC${triggerMsg}`
        : `Auto-send disabled${triggerMsg}.`);
    } catch (_e) {
      setScheduleMsg("Save failed.");
    } finally {
      setSavingSchedule(false);
      setTimeout(() => setScheduleMsg(""), 3000);
    }
  };

  const fetchEmailPreview = async (nl) => {
    setLoadingPreview(p => ({ ...p, [nl.id]: true }));
    try {
      const res = await axios.get(`${API}/admin/newsletters/${nl.id}/preview-html`, { headers: authHeader() });
      setPreviewNlTitle(nl.title);
      setPreviewNlId(nl.id);
      setPreviewHtml(res.data.html);
    } catch (e) {
      toast_error("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingPreview(p => ({ ...p, [nl.id]: false }));
    }
  };

  const generateDraftPreview = async () => {
    setGeneratingDraft(true);
    try {
      const res = await axios.post(
        `${API}/admin/generate-newsletter`,
        { dry_run: true, include_brief: true, format_type: "digest" },
        { headers: authHeader() }
      );
      setPreviewNlTitle(res.data.title || "Draft preview");
      setPreviewNlId(null); // draft: not saved, test-email disabled
      setPreviewHtml(res.data.preview_html);
    } catch (e) {
      toast_error("Draft preview failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingDraft(false);
    }
  };

  const sendTestEmail = async () => {
    if (!previewNlId) return;
    setSendingTestEmail(true);
    try {
      const res = await axios.post(
        `${API}/admin/newsletters/${previewNlId}/send-test-email`,
        {},
        { headers: authHeader() }
      );
      toast_success(`Test email sent to ${res.data.sent_to} via ${res.data.via}`);
    } catch (e) {
      toast_error("Test email failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSendingTestEmail(false);
    }
  };

  const saveNlOgPrompt = async (nlId) => {
    setSavingNlPrompt(p => ({ ...p, [nlId]: true }));
    try {
      await axios.put(`${API}/admin/newsletters/${nlId}`,
        { og_image_prompt: nlPrompts[nlId] || "" },
        { headers: authHeader() }
      );
      await axios.delete(`${API}/admin/og-cache/${nlId}`, { headers: authHeader() });
      toast_success(`OG prompt saved — the image will be regenerated on the next request.`);
    } catch (_e) {
      toast_error("Salvataggio prompt OG fallito");
    } finally {
      setSavingNlPrompt(p => ({ ...p, [nlId]: false }));
    }
  };

  // Simple inline toast helpers (avoid importing toast here to keep component lean)
  const toast_success = (msg) => {
    const el = document.createElement("div");
    el.textContent = msg;
    Object.assign(el.style, { position: "fixed", bottom: "24px", right: "24px", background: "#006874", color: "#fff", padding: "12px 20px", borderRadius: "12px", zIndex: 9999, fontSize: "13px", fontFamily: "sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.15)" });
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  };
  const toast_error = (msg) => {
    const el = document.createElement("div");
    el.textContent = msg;
    Object.assign(el.style, { position: "fixed", bottom: "24px", right: "24px", background: "#dc2626", color: "#fff", padding: "12px 20px", borderRadius: "12px", zIndex: 9999, fontSize: "13px", fontFamily: "sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.15)" });
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  };

  return (
    <TabsContent value="newsletters">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`editorial-heading text-xl sm:text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Newsletter History</h2>
          <p className={`category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{newsletters.length} generated · {newsletters.filter(n => n.is_published).length} published</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateDraftPreview}
            disabled={generatingDraft}
            data-testid="preview-newsletter-draft-btn"
            title="Generate a draft with this week's content and the new template — nothing is saved or sent"
            className={`category-tag flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-400 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}
          >
            {generatingDraft ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
            {generatingDraft ? "Generating…" : "Preview newsletter"}
          </button>
          <button onClick={loadNewsletters} className={`category-tag flex items-center gap-1.5 px-3 py-1.5 rounded-full ${dm ? "bg-[#1a2122] text-slate-400 hover:bg-[#252e2f]" : "bg-[#f3f4f5] hover:bg-[#e7e8e9] text-[#6b7a7d]"}`}>
            <RefreshCw className={`w-3 h-3 ${loadingNL ? "animate-spin" : ""}`} />Refresh
          </button>
        </div>
      </div>

      {/* Auto-Send Schedule */}
      <div className={`rounded-2xl p-4 sm:p-5 mb-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
        {/* Custom editorial note + persistent footer */}
        <div className={`mb-5 pb-5 border-b ${dm ? "border-white/5" : "border-black/5"}`}>
          <button
            type="button"
            onClick={() => setExtrasOpen(o => !o)}
            data-testid="newsletter-extras-toggle"
            className={`flex items-center justify-between w-full text-left ${dm ? "text-slate-200" : "text-[#191c1d]"}`}
          >
            <div className="flex items-center gap-2">
              <PenLine className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
              <h3 className="text-sm font-semibold">Editorial note &amp; footer</h3>
              {extras.editorial_note_md && (
                <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
                  Note staged
                </span>
              )}
              {extras.footer_md && (
                <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-slate-500/10 text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                  Footer active
                </span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${extrasOpen ? "rotate-180" : ""} ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
          </button>
          {extrasOpen && (
            <div className="mt-4 space-y-4" data-testid="newsletter-extras-panel">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                    Editorial note (one-shot — consumed on next newsletter)
                  </label>
                  {extras.editorial_note_md && (
                    <button
                      type="button"
                      onClick={() => setExtras(e => ({ ...e, editorial_note_md: "" }))}
                      className={`category-tag text-xs px-2 py-0.5 rounded-full transition-colors ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <textarea
                  value={extras.editorial_note_md}
                  onChange={e => setExtras(s => ({ ...s, editorial_note_md: e.target.value }))}
                  data-testid="newsletter-editorial-note-textarea"
                  rows={3}
                  maxLength={5000}
                  placeholder={"📱 Big news: the TourismIntel iOS app is live — [Download on the App Store](https://apps.apple.com/...)"}
                  disabled={extrasLoading || extrasSaving}
                  className={`w-full px-3 py-2 rounded-lg text-sm border resize-y font-mono ${dm ? "bg-[#252e2f] border-white/8 text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] border-black/5 text-[#191c1d] placeholder-[#bac9cc]"} disabled:opacity-40`}
                />
                <p className={`category-tag text-xs mt-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                  Markdown supported. Will be prepended at the top of the next generated newsletter and then cleared automatically.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                    Persistent footer (appended to every newsletter)
                  </label>
                  {extras.footer_md && (
                    <button
                      type="button"
                      onClick={() => setExtras(e => ({ ...e, footer_md: "" }))}
                      className={`category-tag text-xs px-2 py-0.5 rounded-full transition-colors ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <textarea
                  value={extras.footer_md}
                  onChange={e => setExtras(s => ({ ...s, footer_md: e.target.value }))}
                  data-testid="newsletter-footer-textarea"
                  rows={3}
                  maxLength={5000}
                  placeholder={"**Get the mobile app**: [App Store](https://apps.apple.com/...) · [Play Store](https://play.google.com/...)"}
                  disabled={extrasLoading || extrasSaving}
                  className={`w-full px-3 py-2 rounded-lg text-sm border resize-y font-mono ${dm ? "bg-[#252e2f] border-white/8 text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] border-black/5 text-[#191c1d] placeholder-[#bac9cc]"} disabled:opacity-40`}
                />
                <p className={`category-tag text-xs mt-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                  Markdown supported. Stays until you change it.
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={saveExtras}
                  disabled={extrasSaving || extrasLoading}
                  data-testid="newsletter-extras-save-btn"
                  className={`category-tag px-4 py-2 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1.5 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
                >
                  {extrasSaving ? <><RefreshCw className="w-3 h-3 animate-spin" />Saving…</> : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Clock className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          <h3 className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Auto-Send Schedule</h3>
          {schedule.last_sent_at && (
            <span className={`ml-auto category-tag text-xs ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
              Last sent: {fmtDatetime(schedule.last_sent_at)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setSchedule(s => ({ ...s, enabled: !s.enabled }))}
              data-testid="newsletter-auto-send-toggle"
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${schedule.enabled ? (dm ? "bg-cyan-400" : "bg-[#006874]") : (dm ? "bg-[#252e2f]" : "bg-[#e8ecee]")}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${schedule.enabled ? "translate-x-5" : ""}`} />
            </div>
            <span className={`category-tag text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{schedule.enabled ? "Enabled" : "Disabled"}</span>
          </label>
          <select
            value={schedule.day_of_week}
            onChange={e => setSchedule(s => ({ ...s, day_of_week: parseInt(e.target.value) }))}
            disabled={!schedule.enabled}
            data-testid="newsletter-schedule-day"
            className={`px-3 py-1.5 rounded-lg text-sm border ${dm ? "bg-[#252e2f] border-white/8 text-slate-200" : "bg-[#f3f4f5] border-black/5 text-[#191c1d]"} disabled:opacity-40`}
          >
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <select
            value={schedule.hour}
            onChange={e => setSchedule(s => ({ ...s, hour: parseInt(e.target.value) }))}
            disabled={!schedule.enabled}
            data-testid="newsletter-schedule-hour"
            className={`px-3 py-1.5 rounded-lg text-sm border ${dm ? "bg-[#252e2f] border-white/8 text-slate-200" : "bg-[#f3f4f5] border-black/5 text-[#191c1d]"} disabled:opacity-40`}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{h.toString().padStart(2, "0")}:00 UTC</option>
            ))}
          </select>
          <button
            onClick={handleSaveSchedule}
            disabled={savingSchedule}
            data-testid="save-newsletter-schedule-btn"
            className={`px-4 py-1.5 rounded-full category-tag text-xs transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-400 hover:bg-cyan-400/25" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
          >
            {savingSchedule ? "Saving…" : "Save"}
          </button>
          {scheduleMsg && <span className={`category-tag text-xs ${dm ? "text-emerald-400" : "text-emerald-600"}`}>{scheduleMsg}</span>}
        </div>
        {/* Trigger on brief publish */}
        <div className={`mt-4 pt-4 border-t flex items-start gap-3 ${dm ? "border-white/6" : "border-black/5"}`}>
          <Switch
            checked={!!schedule.trigger_on_brief_publish}
            onCheckedChange={v => setSchedule(s => ({ ...s, trigger_on_brief_publish: v }))}
            className="data-[state=checked]:bg-[#006874] mt-0.5"
            data-testid="trigger-on-brief-publish-toggle"
          />
          <div>
            <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              Auto-generate + send newsletter when Brief is published
            </p>
            <p className={`category-tag text-xs mt-0.5 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
              When you publish a Brief, a newsletter is automatically generated from recent articles and sent via Brevo.
              Requires Anthropic + Brevo API keys.
            </p>
          </div>
        </div>
        <p className={`category-tag text-xs mt-3 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
          When enabled, the most recently published newsletter is automatically sent via Brevo on the selected day and hour (UTC). The same newsletter is never sent twice.
        </p>
      </div>

      {/* Selected articles for next newsletter (editable persistent queue) */}
      <div className={`rounded-2xl p-4 sm:p-5 mb-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }} data-testid="newsletter-selected-articles-card">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex-1 min-w-0">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              <PenLine className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
              Selected articles for next newsletter
              {nlFeatured.length > 0 && (
                <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]"}`}>
                  {nlFeatured.length} queued
                </span>
              )}
            </h3>
            <p className={`category-tag text-xs mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              Scrape a URL, add it to the feed, and queue it for the next newsletter generation (manual or auto-on-brief-publish). One-shot — cleared after generation.
            </p>
          </div>
          {nlFeatured.length > 0 && (
            <button
              onClick={clearNlFeatured}
              data-testid="clear-nl-featured-btn"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full category-tag text-xs transition-all flex-shrink-0 ${dm ? "text-red-400 hover:bg-red-400/10" : "text-red-500 hover:bg-red-50"}`}
            >
              <X className="w-3 h-3" />Clear all
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="nl-force-url-input"
            value={forceUrl}
            onChange={e => setForceUrl(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !scrapingForce && scrapeForceUrl()}
            placeholder="https://example.com/article"
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
          />
          <button
            onClick={scrapeForceUrl}
            disabled={scrapingForce || !forceUrl.trim()}
            data-testid="nl-force-url-btn"
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
          >
            {scrapingForce ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Scraping…</> : <><Plus className="w-3.5 h-3.5" />Scrape & Add</>}
          </button>
        </div>
        {loadingNlFeatured ? (
          <div className="flex justify-center py-4 mt-3">
            <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          </div>
        ) : nlFeatured.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            {nlFeatured.map((a, i) => (
              <div key={a.id || a.originalUrl || i} data-testid={`nl-featured-row-${i}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>{i + 1}</span>
                <a
                  href={a.originalUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex-1 truncate font-medium hover:underline ${dm ? "text-slate-300" : "text-[#3b494c]"}`}
                >{a.title || a.originalUrl}</a>
                <span className={`flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{a.source}</span>
                <button
                  onClick={() => removeNlFeatured(a.id || a.originalUrl)}
                  data-testid={`remove-nl-featured-${i}`}
                  aria-label="Remove from queue"
                  className={`flex-shrink-0 ${dm ? "text-slate-600 hover:text-red-400" : "text-[#bac9cc] hover:text-red-500"} transition-colors`}
                >
                  <Minus className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Newsletter list */}
      {loadingNL ? (
        <div className="flex justify-center py-16">
          <RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        </div>
      ) : newsletters.length === 0 ? (
        <div className={`rounded-2xl p-16 text-center ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <BookOpen className={`w-8 h-8 mx-auto mb-4 opacity-30 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
          <p className={`editorial-heading text-xl font-medium mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>No newsletters yet</p>
          <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Generate your first newsletter from the feed by selecting articles and clicking &quot;Generate Newsletter&quot;.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {newsletters.map((nl) => (
            <div key={nl.id} data-testid="newsletter-item" className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
              <div className="flex items-start sm:items-center justify-between px-4 sm:px-6 py-4 gap-3 flex-col sm:flex-row">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${nl.is_published ? (dm ? "bg-emerald-400/10" : "bg-emerald-50") : (dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]")}`}>
                    <BookOpen className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${nl.is_published ? (dm ? "text-emerald-400" : "text-emerald-600") : (dm ? "text-slate-500" : "text-[#6b7a7d]")}`} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold truncate ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{nl.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{fmtDate(nl.created_at)}</span>
                      <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{nl.word_count}w · {nl.article_count} art.</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap w-full sm:w-auto flex-shrink-0">
                  {nl.is_published
                    ? <span className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>Published</span>
                    : <span className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-[#252e2f] text-slate-500" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>Draft</span>}
                  <button onClick={() => togglePublish(nl)} className={`px-3 py-1.5 rounded-full category-tag transition-all ${nl.is_published ? (dm ? "bg-amber-400/10 text-amber-400 hover:bg-amber-400/20" : "bg-amber-50 text-amber-700 hover:bg-amber-100") : (dm ? "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100")}`}>
                    {nl.is_published ? "Unpublish" : "Publish"}
                  </button>
                  {nl.is_published && (
                    <a href={`/newsletter/${nl.id}`} target="_blank" rel="noopener noreferrer" title="Public SEO page" className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-[#252e2f] hover:text-cyan-400" : "text-[#bac9cc] hover:bg-[#f3f4f5] hover:text-[#006874]"}`}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => fetchEmailPreview(nl)}
                    disabled={!!loadingPreview[nl.id]}
                    title="Preview email HTML"
                    data-testid={`preview-email-btn-${nl.id}`}
                    className={`px-3 py-1.5 rounded-full category-tag transition-all flex items-center gap-1.5 disabled:opacity-40 ${dm ? "bg-white/5 text-slate-400 hover:bg-white/8" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e7e8e9]"}`}
                  >
                    {loadingPreview[nl.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                    <span className="hidden sm:inline">Preview</span>
                  </button>

                  <button
                    onClick={() => sendBrevoNewsletter(nl, subjectOverrides[nl.id] || "")}
                    disabled={!!sendingCampaign[nl.id] || !nl.is_published}
                    title={nl.is_published ? `Send as Brevo campaign (subject: "${subjectOverrides[nl.id] || nl.title}")` : "Publish first"}
                    data-testid="send-brevo-btn"
                    className={`px-3 py-1.5 rounded-full category-tag transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
                  >
                    {sendingCampaign[nl.id]
                      ? <><RefreshCw className="w-3 h-3 animate-spin" />Sending…</>
                      : <><Send className="w-3 h-3" /><span className="hidden sm:inline">Send via </span>Brevo</>}
                  </button>
                  <button
                    onClick={() => setBeehiivPreview(nl)}
                    disabled={!nl.is_published}
                    title={nl.is_published ? "Preview & publish to Beehiiv" : "Publish first"}
                    data-testid="publish-beehiiv-btn"
                    className={`px-3 py-1.5 rounded-full category-tag transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${dm ? "bg-amber-400/10 text-amber-400 hover:bg-amber-400/20" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                  >
                    <Rss className="w-3 h-3" />Beehiiv
                  </button>
                  <button onClick={() => setExpandedNL(expandedNL === nl.id ? null : nl.id)} className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-[#252e2f] hover:text-slate-300" : "text-[#6b7a7d] hover:bg-[#f3f4f5]"}`}>
                    {expandedNL === nl.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteNewsletter(nl.id)} className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-600 hover:bg-red-400/10 hover:text-red-400" : "text-[#e7e8e9] hover:bg-red-50 hover:text-red-500"}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {expandedNL === nl.id && (
                <div className={`border-t px-6 py-5 space-y-5 ${dm ? "border-white/5" : "border-black/5"}`}>

                  {/* Subject override — edit before sending via Brevo */}
                  <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f8f9fa]"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <PenLine className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                      <span className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Email Subject Line</span>
                    </div>
                    <input
                      type="text"
                      data-testid={`nl-subject-input-${nl.id}`}
                      value={subjectOverrides[nl.id] !== undefined ? subjectOverrides[nl.id] : nl.title}
                      onChange={e => setSubjectOverrides(p => ({ ...p, [nl.id]: e.target.value }))}
                      placeholder={nl.title}
                      className={`w-full rounded-lg px-3 py-2 text-sm border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#1a2122] text-slate-200 placeholder-slate-600" : "bg-white text-[#191c1d] placeholder-[#bac9cc]"}`}
                    />
                    <p className={`category-tag mt-1.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                      Override the email subject used when sending via Brevo. Does not change the stored title.
                    </p>
                  </div>

                  {/* Newsletter content with editorial italic rendering */}
                  <div>{renderNLContent(nl.content, dm)}</div>

                  {/* OG Image customization */}
                  <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f8f9fa]"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Share2 className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                        <span className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Social Preview (OG Image)</span>
                      </div>
                      {nl.is_published && (
                        <a
                          href={`${BACKEND_URL}/api/og/newsletter/${nl.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`preview-nl-og-btn-${nl.id}`}
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs category-tag transition-all ${dm ? "bg-[#1a2122] text-slate-400 hover:text-cyan-400" : "bg-white text-[#6b7a7d] hover:text-[#006874]"}`}
                        >
                          <ExternalLink className="w-3 h-3" />Preview OG
                        </a>
                      )}
                    </div>
                    <p className={`category-tag text-xs mb-3 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                      Istruzioni stile personalizzate — si aggiungono al prompt base Nano Banana (colori, mood, grafica…)
                    </p>
                    <textarea
                      data-testid={`nl-og-prompt-input-${nl.id}`}
                      value={nlPrompts[nl.id] || ""}
                      onChange={e => setNlPrompts(p => ({ ...p, [nl.id]: e.target.value }))}
                      rows={2}
                      placeholder="es. Sfondo verde foresta, accenti bianchi, stile lusso…"
                      className={`w-full resize-none rounded-lg px-3 py-2.5 text-sm border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all mb-3 ${dm ? "bg-[#1a2122] text-slate-200 placeholder-slate-600" : "bg-white text-[#191c1d] placeholder-[#bac9cc]"}`}
                    />
                    <button
                      onClick={() => saveNlOgPrompt(nl.id)}
                      disabled={!!savingNlPrompt[nl.id]}
                      data-testid={`save-nl-og-prompt-btn-${nl.id}`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full category-tag text-xs transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
                    >
                      {savingNlPrompt[nl.id]
                        ? <><RefreshCw className="w-3 h-3 animate-spin" />Salvataggio…</>
                        : <><RefreshCw className="w-3 h-3" />Save & Regenerate OG</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* ── Email HTML Preview Modal ──────────────────────────────────────── */}
      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="email-preview-modal">
          <div className={`relative w-full max-w-4xl h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl ${dm ? "bg-[#161d1e]" : "bg-white"}`}>
            {/* Modal header */}
            <div className={`flex items-center justify-between px-5 py-3 border-b ${dm ? "border-white/8 bg-[#1a2122]" : "border-black/8 bg-[#fafbfb]"}`}>
              <div className="flex items-center gap-2.5">
                <Mail className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                <span className={`text-sm font-semibold truncate max-w-xs ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{previewNlTitle}</span>
                <span data-testid="preview-modal-mode-badge" className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-white/6 text-slate-500" : "bg-[#f3f4f5] text-[#bac9cc]"}`}>{previewNlId ? "Email Preview" : "Draft Preview — not saved"}</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Send Test Email (only for saved newsletters) */}
                {previewNlId && (
                <button
                  onClick={sendTestEmail}
                  disabled={sendingTestEmail}
                  data-testid="send-test-email-btn"
                  title="Send test email to admin address"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-400 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}
                >
                  <FlaskConical className="w-3.5 h-3.5" />
                  {sendingTestEmail ? "Sending…" : "Send Test"}
                </button>
                )}
                <button
                  onClick={() => setMobilePreview(false)}
                  data-testid="desktop-preview-btn"
                  title="Desktop view"
                  className={`p-1.5 rounded-lg transition-all ${!mobilePreview ? (dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]") : (dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]")}`}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setMobilePreview(true)}
                  data-testid="mobile-preview-btn"
                  title="Mobile view"
                  className={`p-1.5 rounded-lg transition-all ${mobilePreview ? (dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]") : (dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]")}`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setPreviewHtml(null); setPreviewNlTitle(""); setPreviewNlId(null); }}
                  data-testid="close-email-preview-btn"
                  className={`p-1.5 rounded-lg transition-all ${dm ? "text-slate-400 hover:text-slate-200 hover:bg-white/6" : "text-[#6b7a7d] hover:text-[#191c1d] hover:bg-black/5"}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Email client mock */}
            <div className={`flex-1 overflow-auto flex items-start justify-center py-6 px-4 ${dm ? "bg-[#252e2f]" : "bg-[#e8ecee]"}`}>
              <div
                className="bg-white rounded-xl overflow-hidden shadow-xl transition-all duration-300"
                style={{ width: mobilePreview ? 375 : "100%", maxWidth: mobilePreview ? 375 : 680, minHeight: 200 }}
              >
                {/* Fake email client top bar */}
                <div className="bg-[#f1f3f4] px-4 py-2.5 border-b border-black/8 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <span className="ml-2 text-xs text-gray-400 font-medium truncate">{previewNlTitle}</span>
                </div>
                <iframe
                  srcDoc={previewHtml}
                  title="Newsletter Email Preview"
                  className="w-full border-0"
                  style={{ height: "70vh", display: "block" }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  );
}
